import React, { useState, useEffect, useCallback } from 'react';
import { createSlot, getAllSlot, updateSlot } from "../Services/masterService";
import Modal from '../AlertModal/Modal';


const initialFormState = {
    startTime: "09:00",
    endTime: "11:00",
    status: "ACTIVE"
};

function ScheduleManager(showAlerts) {
    const [scheduleList, setScheduleList] = useState([]);
    const [formData, setFormData] = useState(initialFormState);
    const [isLoading, setIsLoading] = useState(false);
    const [apiMessage, setApiMessage] = useState({ type: '', text: '' });
    const [editingSlot, setEditingSlot] = useState(null); // track slot being edited
    const [showModal, setShowModal] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalConfig, setModalConfig] = useState({});


    const showSuccess = (msg) => {
        setModalConfig({
            title: "Success!",
            message: msg,
            type: "success",
        });
        setIsModalOpen(true);
    };

    const showError = (titleMsg, message) => {
        setModalConfig({
            title: titleMsg,
            message: message,
            type: "error",
        });
        setIsModalOpen(true);
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const fetchSchedules = useCallback(async () => {
        setIsLoading(true);
        setApiMessage({ type: '', text: '' });
        try {
            const response = await getAllSlot();
            if (response.status === 1) {
                setScheduleList(response.data);
            } else {
                setApiMessage({ type: 'error', text: 'Failed to fetch schedules.' });
            }
        } catch (error) {
            setApiMessage({ type: 'error', text: 'Network error occurred while fetching data.' });
        } finally {
            setIsLoading(false);
        }
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setApiMessage({ type: '', text: '' });
        try {
            if (editingSlot) {
                // Update existing slot
                const request = {
                    startTime: formData.startTime,
                    endTime: formData.endTime,
                    status: formData.status,
                    id: editingSlot.id
                }
                const response = await updateSlot(request);
                if (response.status === 1) {
                    setScheduleList(prev =>
                        prev.map(slot => slot.id === editingSlot.id ? response.data : slot)
                    );
                    setApiMessage({ type: 'success', text: 'Schedule updated successfully!' });
                    showSuccess(`Schedule ${editingSlot ? "updated" : "saved"} successfully!`);
                    setEditingSlot(null);
                    setShowModal(false);
                } else {
                    setApiMessage({ type: 'error', text: response.message || 'Failed to update schedule.' });
                }
            } else {
                // Create new slot
                const response = await createSlot(formData);
                if (response.status === 1) {
                    setScheduleList(prev => [...prev, response.data]);
                    // setApiMessage({ type: 'success', text: 'Schedule added successfully!' });
                    showSuccess(`Schedule ${editingSlot ? "updated" : "saved"} successfully!`);
                    setFormData(initialFormState);
                } else {
                    // setApiMessage({ type: 'error', text: response.message || 'Failed to add schedule.' });
                    showError("API Error", "Operation failed. Server returned status 0.");
                }
            }
        } catch (error) {
            setApiMessage({ type: 'error', text: 'Network error during submission.' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleEditClick = (slot) => {
        setEditingSlot(slot);
        setFormData({
            startTime: slot.startTime,
            endTime: slot.endTime,
            status: slot.status
        });
        setShowModal(true);
    };

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]);

    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="main-wrap">
                <div className="container">
                    <div className="title">
                        <h4>Slot Master</h4>
                    </div>

                    {apiMessage.text && (
                        <div className={`p-3 mb-4 rounded ${apiMessage.type === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {apiMessage.text}
                        </div>
                    )}

                    {/* Form Section */}
                    {!showModal && (
                        <ScheduleForm
                            formData={formData}
                            handleChange={handleChange}
                            handleSubmit={handleSubmit}
                            isLoading={isLoading}
                        />
                    )}

                    {/* Modal for Edit */}
                    {showModal && (
                        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50 p-4 sm:p-6">
                            <div className="bg-white p-6 sm:p-8 rounded-lg shadow-xl w-full max-w-md sm:max-w-lg md:max-w-xl">
                                <h2 className="text-2xl font-semibold mb-4 text-green-700">Edit Schedule Slot</h2>
                                <form onSubmit={handleSubmit} className="form-wrap">
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                        {/* Start Time */}
                                        <div className="w-full">
                                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="startTime">Start Time</label>
                                            <input
                                                type="time"
                                                id="startTime"
                                                name="startTime"
                                                value={formData.startTime}
                                                onChange={handleChange}
                                                className="w-full border border-gray-300 p-2 rounded-md focus:ring-green-500 focus:border-green-500"
                                                required
                                            />
                                        </div>

                                        {/* End Time */}
                                        <div className="w-full">
                                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="endTime">End Time</label>
                                            <input
                                                type="time"
                                                id="endTime"
                                                name="endTime"
                                                value={formData.endTime}
                                                onChange={handleChange}
                                                className="w-full border border-gray-300 p-2 rounded-md focus:ring-green-500 focus:border-green-500"
                                                required
                                            />
                                        </div>

                                        {/* Status */}
                                        <div className="w-full">
                                            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="status">Status</label>
                                            <select
                                                id="status"
                                                name="status"
                                                value={formData.status}
                                                onChange={handleChange}
                                                className="w-full border border-gray-300 p-2 rounded-md focus:ring-green-500 focus:border-green-500 bg-white"
                                            >
                                                <option value="ACTIVE">ACTIVE</option>
                                                <option value="INACTIVE">INACTIVE</option>
                                            </select>
                                        </div>
                                    </div>

                                    {/* Buttons */}
                                    <div className="col-span-1 sm:col-span-2 md:col-span-2 flex flex-col sm:flex-row gap-2 mt-4">
                                        <button
                                            type="submit"
                                            disabled={isLoading}
                                            className={`flex-1 py-2 px-4 rounded-md text-white font-semibold transition duration-150 ${isLoading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'}`}
                                        >
                                            {isLoading ? 'Processing...' : 'Update Schedule'}
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setShowModal(false)}
                                            className="flex-1 py-2 px-4 rounded-md text-gray-700 border border-gray-300 hover:bg-gray-100 transition duration-150"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}


                    {/* List/Table Section */}
                    <ScheduleList
                        showAlerts={{ showSuccess, showError }}
                        data={scheduleList}
                        isLoading={isLoading}
                        onEditClick={handleEditClick}
                    />


                    <Modal
                        isOpen={isModalOpen}
                        onClose={() => setIsModalOpen(false)}
                        title={modalConfig.title}
                        message={modalConfig.message}
                        type={modalConfig.type}
                    />

                </div>
            </div>
        </div>
    );
}

const ScheduleForm = ({ formData, handleChange, handleSubmit, isLoading }) => (
    <div className="bg-white p-6 rounded-lg shadow-xl mb-8 border-t-4 border-green-600">
        <h2 className="text-2xl font-semibold mb-4 text-green-700">Add New Schedule Slot</h2>
        <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="startTime">Start Time</label>
                <input
                    type="time"
                    id="startTime"
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleChange}
                    className="w-full border border-gray-300 p-2 rounded-md focus:ring-green-500 focus:border-green-500"
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="endTime">End Time</label>
                <input
                    type="time"
                    id="endTime"
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleChange}
                    className="w-full border border-gray-300 p-2 rounded-md focus:ring-green-500 focus:border-green-500"
                    required
                />
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="status">Status</label>
                <select
                    id="status"
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full border border-gray-300 p-2 rounded-md focus:ring-green-500 focus:border-green-500 bg-white"
                >
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="INACTIVE">INACTIVE</option>
                </select>
            </div>

            <button
                type="submit"
                disabled={isLoading}
                className={`w-full py-2 px-4 rounded-md text-white font-semibold transition duration-150 ${isLoading ? 'bg-green-400 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700'
                    }`}
            >
                {isLoading ? 'Processing...' : 'Create'}
            </button>
        </form>
    </div>
);

const ScheduleList = ({ data, isLoading, onEditClick }) => {
    if (isLoading && data.length === 0) {
        return <p className="p-4 text-center text-green-600 font-medium">Loading schedules...</p>;
    }

    if (!data || data.length === 0) {
        return <p className="p-4 text-center text-gray-500">No schedules recorded yet.</p>;
    }

    const columns = ["ID", "Start Time", "End Time", "Status", "Actions"];

    return (
        <div className="bg-white p-6 rounded-lg shadow-xl border-t-4 border-green-600">
            <h2 className="text-2xl font-semibold mb-4 text-green-700">Current Schedule List ({data.length})</h2>
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-green-50">
                        <tr>
                            {columns.map(col => (
                                <th key={col} className="px-6 py-3 text-left text-xs font-medium text-green-800 uppercase tracking-wider">
                                    {col}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {data.map((item) => (
                            <tr key={item.id} className="hover:bg-gray-50">
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{item.id}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.startTime}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">{item.endTime}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm">
                                    {item.status && item.status.toUpperCase() === 'ACTIVE'
                                        ? <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">ACTIVE</span>
                                        : <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">INACTIVE</span>
                                    }
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium flex gap-1">
                                    <button
                                        onClick={() => onEditClick(item)}
                                        className=" text-white font-sm py-2 px-3 rounded text-sm theme_btn"
                                    >
                                        Edit
                                    </button>
                                    <button
                                        onClick={() => alert("Work in progress!")}
                                        className=" text-white font-sm py-2 px-3 rounded text-sm cancel_btn"
                                    >
                                        Delete
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>


        </div>
    );
};

export default ScheduleManager;
